/**
 * Pure helpers for the Sales tab tiles (quick-261104-ia3 follow-up, 2026-11-04).
 * Kept out of the components so they can be unit-tested.
 */
import { SERIES, INK } from './chart.js';

import type { ExecCyclePanel, ExecCycleQuarter, ExecFunnelPanel, ExecWinRateQuarter } from '@saledash/shared';

export type DealType = 'new' | 'exp';
export type CycleType = 'all' | 'new' | 'exp';

export interface AvgDealPoint {
  label: string;
  /** Average won deal $ that quarter, or null when nothing was won. */
  avg: number | null;
  won: number;
  amt: number;
}

/** True when the win-rate quarters carry closed-won $ (newer cache rows). */
export function hasWonAmounts(quarters: ExecWinRateQuarter[] | undefined): boolean {
  return (quarters ?? []).some((q) => q.newWonAmt != null || q.expWonAmt != null);
}

/** Average won deal by quarter for one deal type: won $ ÷ won count. */
export function avgDealByQuarter(quarters: ExecWinRateQuarter[], type: DealType): AvgDealPoint[] {
  return quarters.map((q) => {
    const won = type === 'new' ? q.newWon : q.expWon;
    const amt = (type === 'new' ? q.newWonAmt : q.expWonAmt) ?? 0;
    return { label: q.label, won, amt, avg: won > 0 ? amt / won : null };
  });
}

/** Trailing average over the last `k` quarters: total won $ ÷ total won count. */
export function trailingAvgDeal(points: AvgDealPoint[], k = 4): number | null {
  const tail = points.slice(-k);
  const won = tail.reduce((s, p) => s + p.won, 0);
  const amt = tail.reduce((s, p) => s + p.amt, 0);
  return won > 0 ? amt / won : null;
}

/** Current open-deal average for one type, or null when unknown / no open deals. */
export function openAvgDeal(open: ExecFunnelPanel['openByType'], type: DealType): number | null {
  if (!open) return null;
  const amt = type === 'new' ? open.newAmt : open.expAmt;
  const n = type === 'new' ? open.newN : open.expN;
  return n > 0 ? amt / n : null;
}

/** SF Opportunity.Type → the short table label. */
export function dealTypeShort(type: string | null | undefined): string {
  if (type === 'New Business') return 'New';
  if (type === 'Existing Business') return 'Exp';
  return '—';
}

/** Sales-cycle quarters for a type; `all` falls back to the panel's base series. */
export function cycleQuarters(cycle: ExecCyclePanel | undefined, type: CycleType): ExecCycleQuarter[] {
  if (!cycle) return [];
  if (cycle.byType) return cycle.byType[type];
  return type === 'all' ? cycle.quarters : [];
}

/** Deal-weighted average of a cycle field across quarters (null when no deals). */
export function pooledCycle(qs: ExecCycleQuarter[], key: 'all' | 'large' | 'small'): number | null {
  const nKey = `${key}N` as const;
  const n = qs.reduce((s, q) => s + q[nKey], 0);
  if (n === 0) return null;
  return qs.reduce((s, q) => s + (q[key] ?? 0) * q[nKey], 0) / n;
}

/** Colours for sub-groups of a breakdown, assigned by index (fixed categorical order). */
const GROUP_COLORS = [SERIES.blue, SERIES.orange, SERIES.aqua, SERIES.violet, '#c2410c', INK.muted];
export function groupColor(i: number): string {
  return GROUP_COLORS[i % GROUP_COLORS.length] ?? INK.muted;
}

/** True for the MAIN-25 bucket that holds the free-text "Other" losses. */
export function isOtherReason(label: string): boolean {
  return label.trim().toLowerCase() === 'other';
}
